import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Select, Switch, Button, Space, Spin, Alert, message, Descriptions, Tag } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getTenantDomain,
  createTenantDomain,
  updateTenantDomain,
  type CreateTenantDomainRequest,
  type UpdateTenantDomainRequest,
} from '@/api/adminApi';
import { useAuth } from '@/hooks/useAuth';

const VERIFICATION_METHODS = ['dns_txt', 'dns_cname'];

interface DomainFormValues {
  domain: string;
  is_primary: boolean;
  verification_method: string;
}

export default function DomainForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<DomainFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [domainRecord, setDomainRecord] = useState<{ verification_token?: string; status?: string } | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getTenantDomain(id)
      .then((d) => {
        form.setFieldsValue({
          domain: d.domain,
          is_primary: d.is_primary,
          verification_method: d.verification_method,
        });
        setDomainRecord({ verification_token: d.verification_token, status: d.status });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t]);

  const handleSubmit = async (values: DomainFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        const dto: UpdateTenantDomainRequest = {
          is_primary: values.is_primary,
          verification_method: values.verification_method,
        };
        await updateTenantDomain(id, dto);
        message.success(t('admin.domain_update_success'));
      } else {
        const dto: CreateTenantDomainRequest = {
          domain: values.domain,
          is_primary: values.is_primary,
          verification_method: values.verification_method,
        };
        await createTenantDomain(dto);
        message.success(t('admin.domain_create_success'));
      }
      navigate('/admin/domains');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('app.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasPermission('admin', 'write')) {
    return <Alert type="error" message={t('app.forbidden')} />;
  }

  if (loading) return <Spin size="large" />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/domains')}>
          {t('app.back')}
        </Button>
      </Space>

      <h2>{isEdit ? t('admin.edit_domain_title') : t('admin.create_domain_title')}</h2>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      {isEdit && domainRecord?.verification_token && (
        <Descriptions bordered column={1} style={{ marginBottom: 16 }}>
          <Descriptions.Item label={t('admin.domain_field.verification_token')}>
            <code>{domainRecord.verification_token}</code>
          </Descriptions.Item>
          <Descriptions.Item label={t('admin.domain_field.status')}>
            <Tag color={domainRecord.status === 'verified' ? 'success' : domainRecord.status === 'failed' ? 'error' : 'processing'}>
              {t(`admin.domain_status.${domainRecord.status}`, domainRecord.status ?? '')}
            </Tag>
          </Descriptions.Item>
        </Descriptions>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ is_primary: false, verification_method: 'dns_txt' }}
        style={{ maxWidth: 600 }}
      >
        <Form.Item
          name="domain"
          label={t('admin.domain_field.domain')}
          rules={[{ required: true, message: t('admin.validation.domain_required') }]}
        >
          <Input disabled={isEdit} placeholder="example.com" />
        </Form.Item>

        <Form.Item name="verification_method" label={t('admin.domain_field.verification_method')}>
          <Select>
            {VERIFICATION_METHODS.map((m) => (
              <Select.Option key={m} value={m}>
                {t(`admin.domain_verification.${m}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="is_primary" label={t('admin.domain_field.is_primary')} valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate('/admin/domains')}>{t('app.cancel')}</Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
